import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { COMPANY, SERVICES } from "../data";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [privacy, setPrivacy] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Bitte Name und Telefon angeben.");
      return;
    }
    if (!privacy) {
      toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, privacy_accepted: privacy });
      toast.success("Vielen Dank! Ihre Anfrage wurde gesendet. Wir melden uns innerhalb von 24 Stunden.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
      setPrivacy(false);
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte rufen Sie uns direkt an: 09744 / 766");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full rounded-md border border-[#DDD9D0] bg-white px-4 py-3 text-sm outline-none focus:border-[#2A5C2A] focus:ring-2 focus:ring-[#2A5C2A]/20 transition-colors";

  return (
    <section id="kontakt" className="bg-[#F5F4F1] py-24 sm:py-32 overflow-hidden" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.h2
          className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A3C1A]"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          data-testid="contact-headline"
        >
          Sprechen Sie uns an.
        </motion.h2>

        <div className="mt-14 grid lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            data-testid="contact-direct-info"
          >
            <a href={`tel:${COMPANY.phoneLink}`} className="flex items-center gap-4 group" data-testid="contact-phone-main">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#2A5C2A] text-white">
                <Phone size={24} />
              </span>
              <span className="font-heading text-3xl sm:text-4xl font-bold text-[#1A3C1A] group-hover:text-[#2A5C2A] transition-colors">
                {COMPANY.phone}
              </span>
            </a>

            <div className="mt-8 space-y-3 text-[#2A2A2A]">
              <p><span className="font-semibold">Andreas Müller:</span>{" "}
                <a href={`tel:${COMPANY.mobileAndreasLink}`} className="text-[#2A5C2A] hover:underline" data-testid="contact-mobile-andreas">{COMPANY.mobileAndreas}</a>
              </p>
              <p><span className="font-semibold">Richard Müller:</span>{" "}
                <a href={`tel:${COMPANY.mobileRichardLink}`} className="text-[#2A5C2A] hover:underline" data-testid="contact-mobile-richard">{COMPANY.mobileRichard}</a>
              </p>
              <p className="flex items-center gap-2">
                <Mail size={16} className="text-[#2A5C2A]" />
                <a href={`mailto:${COMPANY.email}`} className="text-[#2A5C2A] hover:underline" data-testid="contact-email">{COMPANY.email}</a>
              </p>
              <p className="flex items-center gap-2" data-testid="contact-address">
                <MapPin size={16} className="text-[#2A5C2A]" /> {COMPANY.address}
              </p>
            </div>

            <div className="mt-8 rounded-lg overflow-hidden border border-[#DDD9D0]">
              <iframe
                title="Zimmerei Müller Standort"
                src="https://maps.google.com/maps?q=Talstra%C3%9Fe%205%2C%2097799%20Zeitlofs&z=14&output=embed"
                className="w-full h-64"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-maps-embed"
              />
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            className="bg-white rounded-lg border border-[#ECEAE4] p-7 sm:p-9 shadow-sm h-fit"
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold mb-1.5">Name *</label>
                <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Ihr Name" data-testid="contact-input-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Telefon *</label>
                <input required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Ihre Telefonnummer" data-testid="contact-input-phone" />
              </div>
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold mb-1.5">E-Mail</label>
              <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="ihre@email.de" data-testid="contact-input-email" />
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold mb-1.5">Leistung gewünscht</label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger className="w-full h-[46px] border-[#DDD9D0]" data-testid="contact-select-service">
                  <SelectValue placeholder="Bitte wählen…" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.title} value={s.title} data-testid={`service-option-${s.title.toLowerCase().slice(0, 10).replace(/\s/g, "-")}`}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold mb-1.5">Kurze Beschreibung / Nachricht</label>
              <textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Beschreiben Sie kurz Ihr Vorhaben…" data-testid="contact-input-message" />
            </div>
            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <Checkbox checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5 data-[state=checked]:bg-[#2A5C2A] data-[state=checked]:border-[#2A5C2A]" data-testid="contact-privacy-checkbox" />
              <span className="text-sm text-[#666666]">
                Ich habe die <a href="/datenschutz" className="text-[#2A5C2A] underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zu. *
              </span>
            </label>
            <button
              type="submit"
              disabled={sending}
              className="btn-green mt-7 w-full rounded-full py-3.5 font-semibold border border-[#2A5C2A] flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {sending && <Loader2 size={18} className="animate-spin" />} Anfrage absenden
            </button>
            <p className="mt-3 text-center text-xs text-[#666666]">Wir melden uns innerhalb von 24 Stunden.</p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
